import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CPDropdown } from "../../CPDropdown";
import { CPInput } from "../../CPInput";
import { useMembership } from "../../../context/MembershipContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { CAPABILITY } from "../../../helpers/permissions";
import { getDefaultOrganizationId } from "../../../helpers/organizationHelpers";
import {
  canInviteRole,
  createOrganizationInvitation,
  DEFAULT_INVITATION_TTL_DAYS,
  INVITABLE_ROLES,
  INVITATION_STATUS,
  listOrganizationInvitations,
  revokeOrganizationInvitation,
} from "../../../helpers/membershipInvitations";
import { AdminFormCard } from "../shared/AdminFormCard";
import { AdminLegacyHeader } from "../shared/AdminFormCard";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskTable } from "../../desktop/DeskTable";
import "../adminDesktop.css";

function invitationTone(status) {
  switch (status) {
    case INVITATION_STATUS.PENDING:
      return "gold";
    case INVITATION_STATUS.ACCEPTED:
      return "green";
    case INVITATION_STATUS.REVOKED:
      return "red";
    case INVITATION_STATUS.EXPIRED:
      return "blue";
    default:
      return "muted";
  }
}

const EXPIRATION_OPTIONS = [
  { value: String(7), label: "7 days" },
  { value: String(14), label: "14 days" },
  { value: String(30), label: "30 days" },
];

function buildInvitationLink(token) {
  if (typeof window === "undefined" || !token) {
    return "";
  }
  const url = new URL(window.location.href);
  url.searchParams.set("invite", token);
  url.searchParams.delete("invitation");
  return url.toString();
}

function formatInvitationStatus(status) {
  if (!status) {
    return "Unknown";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function invitationStatusBadgeClass(status) {
  switch (status) {
    case INVITATION_STATUS.PENDING:
      return "admin-status-badge admin-status-badge--pending";
    case INVITATION_STATUS.ACCEPTED:
      return "admin-status-badge admin-status-badge--accepted";
    case INVITATION_STATUS.REVOKED:
      return "admin-status-badge admin-status-badge--revoked";
    case INVITATION_STATUS.EXPIRED:
      return "admin-status-badge admin-status-badge--expired";
    default:
      return "admin-status-badge";
  }
}

async function copyTextToClipboard(text) {
  if (!text) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function AdminInvitationsPanel({ onBack, showLegacyHeader = false }) {
  const { membership, organizationId, isOrgAdmin, loading: membershipLoading } =
    useMembership();
  const { can } = usePermissions();
  const isDesktop = useIsDesktopViewport();

  const canManageInvitations =
    isOrgAdmin || can(CAPABILITY.ADMIN_ORGANIZATION_MANAGE);

  const effectiveMembership = useMemo(() => {
    if (membership?.role) {
      return membership;
    }
    if (can(CAPABILITY.ADMIN_ORGANIZATION_MANAGE)) {
      return { role: "owner", id: membership?.id ?? null };
    }
    return membership;
  }, [membership, can]);

  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [ttlDays, setTtlDays] = useState(String(DEFAULT_INVITATION_TTL_DAYS));
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(true);

  const loadInvitations = useCallback(async () => {
    let defaultOrgId = null;

    if (canManageInvitations) {
      try {
        defaultOrgId = await getDefaultOrganizationId();
      } catch {
        // Fall back to membership context organizationId only.
      }
    }

    const resolvedOrgId = organizationId || defaultOrgId;
    if (!resolvedOrgId) {
      return;
    }

    setListLoading(true);
    try {
      const rows = await listOrganizationInvitations(resolvedOrgId, { status: null });
      setInvitations(rows);
      setError(null);
    } catch (err) {
      console.error("[AdminInvitations] load failed", err);
      setError(err.message || "Unable to load invitations.");
    } finally {
      setListLoading(false);
    }
  }, [organizationId, canManageInvitations]);

  useEffect(() => {
    if (membershipLoading) {
      return;
    }
    loadInvitations();
  }, [membershipLoading, loadInvitations]);

  const handleCreate = async () => {
    setError(null);
    setMessage(null);

    if (!canManageInvitations) {
      setError("You are not allowed to create invitations.");
      return;
    }

    if (!canInviteRole(effectiveMembership, role)) {
      setError("You are not allowed to invite users with this role.");
      return;
    }

    setLoading(true);
    try {
      let defaultOrgId = null;
      if (!organizationId && canManageInvitations) {
        defaultOrgId = await getDefaultOrganizationId();
      }
      const orgId = organizationId || defaultOrgId;
      if (!orgId) {
        setError("Organization context is not ready. Please try again.");
        return;
      }

      const created = await createOrganizationInvitation({
        organizationId: orgId,
        email,
        role,
        invitedByMemberId: effectiveMembership?.id ?? null,
        ttlDays: Number(ttlDays) || DEFAULT_INVITATION_TTL_DAYS,
      });
      setEmail("");
      setMessage(`Invitation created for ${created.email}.`);
      await loadInvitations();
    } catch (err) {
      setError(err.message || "Unable to create invitation.");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (invitationId) => {
    setError(null);
    setMessage(null);
    try {
      await revokeOrganizationInvitation(invitationId);
      setMessage("Invitation revoked.");
      setInvitations((prev) =>
        prev.map((row) =>
          row.id === invitationId
            ? { ...row, status: INVITATION_STATUS.REVOKED }
            : row,
        ),
      );
      await loadInvitations();
    } catch (err) {
      setError(err.message || "Unable to revoke invitation.");
    }
  };

  const handleCopy = async (label, text) => {
    const copied = await copyTextToClipboard(text);
    if (copied) {
      setMessage(`${label} copied to clipboard.`);
    } else {
      setError(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const inviteRoleOptions = INVITABLE_ROLES.filter((option) =>
    canInviteRole(effectiveMembership, option.value),
  );

  const desktopContent = (
    <>
      {!canManageInvitations && (
        <p className="admin-placeholder-note">
          Only organization owners and admins can manage invitations.
        </p>
      )}
      <div className="adm-main-side">
        <div className="adm-stack">
          {listLoading && invitations.length === 0 ? (
            <p className="adm-note">Loading invitations…</p>
          ) : invitations.length === 0 ? (
            <DeskEmpty title="No invitations yet." />
          ) : (
            <DeskTable
              caption="Invitations"
              rows={invitations}
              rowKey={(invitation) => invitation.id}
              rowClassName={(invitation) =>
                invitation.status === INVITATION_STATUS.PENDING ? "" : "is-muted"
              }
              columns={[
                {
                  key: "email",
                  label: "Invitee",
                  render: (invitation) => (
                    <>
                      <span className="dt-strong">{invitation.email}</span>
                      {invitation.status === INVITATION_STATUS.PENDING ? (
                        <span className="dt-sub adm-mono adm-token">
                          Token {invitation.token}
                        </span>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: "role",
                  label: "Role",
                  width: "110px",
                  render: (invitation) => (
                    <span className="admin-invitation-role">{invitation.role}</span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  width: "110px",
                  render: (invitation) => (
                    <DeskPill tone={invitationTone(invitation.status)}>
                      {formatInvitationStatus(invitation.status)}
                    </DeskPill>
                  ),
                },
                {
                  key: "expires",
                  label: "Expires",
                  width: "150px",
                  render: (invitation) => (
                    <span className="dt-muted">
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  align: "right",
                  width: "230px",
                  render: (invitation) =>
                    invitation.status === INVITATION_STATUS.PENDING ? (
                      <span className="adm-rowact">
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() =>
                            handleCopy("Invitation link", buildInvitationLink(invitation.token))
                          }
                        >
                          Copy invitation link
                        </button>
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => handleCopy("Invitation token", invitation.token)}
                        >
                          Copy invitation token
                        </button>
                        {canManageInvitations && (
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm adm-danger"
                            onClick={() => handleRevoke(invitation.id)}
                          >
                            Revoke
                          </button>
                        )}
                      </span>
                    ) : null,
                },
              ]}
            />
          )}
          {error && <p className="admin-placeholder-note">{error}</p>}
          {message && <p className="admin-save-confirmation">{message}</p>}
        </div>

        {canManageInvitations && (
          <AdminFormCard title="INVITE MEMBER">
            <div className="adm-fields adm-fields--1">
              <CPInput
                label="Email address"
                value={email}
                onChange={setEmail}
                type="email"
                placeholder="employee@company.com"
              />
              <CPDropdown label="Role" value={role} onChange={setRole} options={inviteRoleOptions} />
              <CPDropdown
                label="Expiration (optional)"
                value={ttlDays}
                onChange={setTtlDays}
                options={EXPIRATION_OPTIONS}
              />
            </div>
            <div className="adm-actions">
              <button
                type="button"
                className="kpi-back-btn adm-primary"
                onClick={handleCreate}
                disabled={loading || !email.trim()}
              >
                {loading ? "Creating…" : "Create Invitation"}
              </button>
            </div>
            <p className="admin-placeholder-note" style={{ marginTop: "14px", marginBottom: 0 }}>
              The invitation appears in the list. Share the invitation link or
              token with the invitee after they have a CrewPilot account.
            </p>
          </AdminFormCard>
        )}
      </div>
    </>
  );

  const content = isDesktop ? desktopContent : (
    <>
      {!canManageInvitations && (
        <p className="admin-placeholder-note">
          Only organization owners and admins can manage invitations.
        </p>
      )}

      {canManageInvitations && (
        <>
          <div style={{ marginBottom: "12px" }}>
            <button
              type="button"
              className="kpi-back-btn"
              onClick={() => setShowInviteForm((open) => !open)}
              aria-expanded={showInviteForm}
            >
              {showInviteForm ? "− Invite Member" : "+ Invite Member"}
            </button>
          </div>

          {showInviteForm && (
            <AdminFormCard title="INVITE MEMBER">
              <CPInput
                label="Email address"
                value={email}
                onChange={setEmail}
                type="email"
                placeholder="employee@company.com"
              />

              <CPDropdown
                label="Role"
                value={role}
                onChange={setRole}
                options={inviteRoleOptions}
              />

              <CPDropdown
                label="Expiration (optional)"
                value={ttlDays}
                onChange={setTtlDays}
                options={EXPIRATION_OPTIONS}
              />

              <button
                type="button"
                className="kpi-back-btn"
                onClick={handleCreate}
                disabled={loading || !email.trim()}
              >
                {loading ? "Creating…" : "Create Invitation"}
              </button>

              <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
                The invitation appears in the list below. Share the invitation link
                or token with the invitee after they have a CrewPilot account.
              </p>
            </AdminFormCard>
          )}
        </>
      )}

      <AdminFormCard title="INVITATIONS">
        {listLoading && invitations.length === 0 ? (
          <p className="profile-value">Loading invitations…</p>
        ) : invitations.length === 0 ? (
          <p className="profile-value">No invitations yet.</p>
        ) : (
          invitations.map((invitation) => {
            const isPending = invitation.status === INVITATION_STATUS.PENDING;
            const inviteLink = buildInvitationLink(invitation.token);

            return (
              <div key={invitation.id} className="admin-invitation-card">
                <div className="admin-invitation-card-header">
                  <span className="admin-data-primary">{invitation.email}</span>
                  <span className="admin-data-secondary admin-invitation-role">
                    {invitation.role}
                  </span>
                </div>
                <div className="admin-invitation-card-meta">
                  <span className={invitationStatusBadgeClass(invitation.status)}>
                    {formatInvitationStatus(invitation.status)}
                  </span>
                  <span className="admin-data-secondary">
                    Expires {new Date(invitation.expires_at).toLocaleString()}
                  </span>
                </div>
                {isPending && (
                  <>
                    <div className="admin-data-meta">
                      <span className="admin-data-label">Token </span>
                      {invitation.token}
                    </div>
                    <div className="admin-invitation-actions">
                      <button
                        type="button"
                        className="kpi-back-btn"
                        onClick={() => handleCopy("Invitation link", inviteLink)}
                      >
                        Copy invitation link
                      </button>
                      <button
                        type="button"
                        className="kpi-back-btn"
                        onClick={() =>
                          handleCopy("Invitation token", invitation.token)
                        }
                      >
                        Copy invitation token
                      </button>
                      {canManageInvitations && (
                        <button
                          type="button"
                          className="kpi-back-btn"
                          onClick={() => handleRevoke(invitation.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </AdminFormCard>

      {error && <p className="admin-placeholder-note">{error}</p>}
      {message && <p className="admin-save-confirmation">{message}</p>}
    </>
  );

  if (showLegacyHeader) {
    return (
      <div className="intel-screen">
        <AdminLegacyHeader title="ORGANIZATION INVITATIONS" onBack={onBack} />
        {content}
      </div>
    );
  }

  return content;
}
